import { Box, Button, CircularProgress, ListItemButton, Typography } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { isBrowser, isMobileOnly } from "react-device-detect";
import { useNavigate, useSearchParams } from "react-router-dom";
import { deleteAPIAsync, getAPI, postAPIAsync } from "../api/apiCalls";
import Provider from "../api/provider";
import { EntityType, icon, inIframe, isNullOrEmpty, MAX_RECORD, removeSpecialChars, UserStats, WEBSITE_PATH, windowPostMessage } from "../common/common";
import { Encrypt } from "../common/reacte2ee";
import { resource } from "../common/resource";
import ListCards, { CardListForSuggestion, SetUserData } from "../components/cardsList";
import CreateMenuHeader, { CreateHeaderInbox } from "../components/headers";
import InboxSkeleton from "../components/skeletonLoaders/inboxSkeleton";
import { StyledBadgeNotification } from "../components/styledBadge";
import { TooltipMUI } from "../components/tooltip";
import { UserDetailsContext } from "../contexts/entityDetailsContext";
import { Connection } from "../models/connector";
import { FollowingAccountsResponse, SuggestedResponse } from "../models/inbox";
import { ChatHistory, MessageData } from "../models/messaging";
import Connector from "../signalr/signalr-connection";
import "../styles/inbox.css";

const Inbox = () => {
  const navigate = useNavigate();
  const [searchParam] = useSearchParams();
  const { userDetails, setUserDetails } = useContext<any>(UserDetailsContext);
  const [connectivityStatus, setConnectivityStatus] = useState<boolean>(false);
  const [newChat, setNewChat] = useState<boolean>(false);
  const [chatHistory, setChatHistory] = useState<ChatHistory[]>([]);
  const [suggestion, setSuggestion] = useState<SuggestedResponse[]>([]);
  const [isAjax, setIsAjax] = useState<boolean>(false);
  const [pageNo, setPageNo] = useState<number>(0);
  const [loader, setLoader] = useState<boolean>(false);
  const [loaderPage, setLoaderPage] = useState(true);
  const [newFollowersCount, setNewFollowersCount] = useState<number>(0);
  const [newFollowersName, setNewFollowersName] = useState<string>();
  const [longPressData, setLongPressData] = useState<ChatHistory[]>([]);
  const [mobMenuOptions, setMobMenuOptions] = useState<any>();
  const hideMenuRef: any = useRef();
  const usercodenum: any = useRef();
  const tempUserDetails: any = useRef();
  const conversationToDelete: any = useRef();
  const chatHistoryRef: any = useRef();
  //#region useEffect
  useEffect(() => {
    if (!inIframe()) {
      window.location.href = WEBSITE_PATH;
      return;
    }
    usercodenum.current = encodeURIComponent(searchParam.get("userId"));
    if (isNullOrEmpty(userDetails)) {
      windowPostMessage({ type: "getuserdetails" }, WEBSITE_PATH);
    } else {
      commonFunctionCalls(usercodenum.current);
    }

    windowPostMessage({ type: "updateNotification" }, WEBSITE_PATH);

    window.addEventListener("message", handleWindowMessageReceived);

    return () => {
      window.removeEventListener("message", handleWindowMessageReceived);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    chatHistoryRef.current = chatHistory;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatHistory]);

  useEffect(() => {
    userDetails && getUserInboxConversation(userDetails.ucnum, pageNo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails]);

  useEffect(() => {
    pageNo !== 0 && userDetails && getUserInboxConversation(userDetails.ucnum, pageNo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo]);

  useEffect(() => {
    if (longPressData.length > 0) {
      let pinIcon = icon({ iconName: "keep_off" }),
        pinType = "unpin";
      if (!isNullOrEmpty(longPressData.find((el) => el.pinnedAt === 0))) {
        pinIcon = icon({ iconName: "keep" });
        pinType = "pin";
      }
      let muteIcon = icon({ iconName: "notifications_active" }),
        muteType = "unmute";
      if (!isNullOrEmpty(longPressData.find((el) => !(el.user && el.user.isMuted)))) {
        muteIcon = icon({ iconName: "notifications_off" });
        muteType = "mute";
      }

      let menuData: any = [
        {
          icon: pinIcon,
          callback: () => {
            onPinUnpinMenuCallback(pinType);
          },
        },
        {
          icon: muteIcon,
          callback: () => {
            onMuteUnmuteMenuCallback(muteType);
          },
        },
        {
          icon: icon({ iconName: "delete" }),
          callback: onDeleteMenuCallback,
        },
      ];

      longPressData.length === 1 &&
        menuData.push({
          icon: icon({ iconName: "more_vert" }),
          menuOnClick: [
            {
              icon: icon({ iconName: "block", iconCustomClass: `${longPressData[0].user?.followingStatus === UserStats.USER_HAS_BLOCKED ? "rotate90" : ""}` }),
              title: longPressData[0].user?.followingStatus === UserStats.USER_HAS_BLOCKED ? "Unblock" : "Block",
              callback: () => {
                if (longPressData[0].user && longPressData[0].user.usercodenumdynamic) windowPostMessage({ type: "userBlockUnblocked", ucnum: longPressData[0].user.usercodenumdynamic, fullname: longPressData[0].user.name, username: longPressData[0].user.displayUsername, blockType: longPressData[0].user?.followingStatus === UserStats.USER_HAS_BLOCKED ? "unblock" : "block", callType: "inbox", entityType: longPressData[0].user.type, data: longPressData[0] }, WEBSITE_PATH);
              },
            },
          ],
        });

      setMobMenuOptions(menuData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [longPressData]);
  //#endregion

  //#region Api calls
  const getUserConnectivityStatus = (usercodenum: string) => {
    getAPI(Provider.URLS.BASE_URL, Provider.API_URLS.UserConnectivityStatus, { entityCodeNum: usercodenum, EntityCodeType: 1 }, (data: any) => setConnectivityStatus(data.data.status));
  };

  const getUserNewFollowersCount = (usercodenum: string) => {
    getAPI(Provider.URLS.BASE_URL, Provider.API_URLS.NewFollowerCount, { ucnum: usercodenum }, (data: any) => {
      setNewFollowersCount(parseInt(data.data.count));
      if (!isNullOrEmpty(data.data.fullname)) {
        setNewFollowersName(data.data.fullname);
      }
    });
  };
  const getUserInboxConversation = (usercodenum: string, pageNoVal: number) => {
    getAPI(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.InboxHistory,
      {
        ucnum: usercodenum,
        pageSize: MAX_RECORD,
        pageNo: pageNoVal,
        entityType: EntityType.USER,
        status: 4, // ConversationStatus.APPROVED,
      },
      (response: any) => {
        let data = response.data;
        if (data) {
          data = response.data.conversationsResponse;
          if (chatHistory.length > 0) {
            setChatHistory([...chatHistory, ...data]);
          } else setChatHistory(data);
          setIsAjax(!(data.length < MAX_RECORD));
        } else {
          setIsAjax(false);
        }
        if (pageNo === 0) {
          windowPostMessage({ type: "getCacheSuggestion" }, WEBSITE_PATH);
        }
        setLoader(false);
        setLoaderPage(false);
      },
      (e: any) => {
        setLoaderPage(false);
        setLoader(false);
        setIsAjax(false);
        setChatHistory([]);
        windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, WEBSITE_PATH);
      }
    );
  };

  const getUserSuggestionList = (usercodenum: string) => {
    getAPI(Provider.URLS.BASE_URL, Provider.API_URLS.Suggestion, { EntityCodeNum1: encodeURIComponent(usercodenum), size: 20 }, (response: any) => {
      const data: SuggestedResponse[] = response.data;
      if (data && data.length > 0) {
        setSuggestion(data);
        windowPostMessage({ type: "cacheSuggestion", data: data }, WEBSITE_PATH);
      }
    });
  };

  const getUserFollowingList = (usercodenum: string) => {
    getAPI(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.UserFollowing,
      {
        ucnum: usercodenum,
        isAllPostsCount: true,
        searchText: "",
        pageSize: 1,
        pageNo: 0,
      },
      (response: any) => {
        const data: FollowingAccountsResponse[] = response.data;
        if (data && data[0]?.totalFollowers && data[0]?.totalFollowers > 0) {
          setNewChat(true);
        }
      }
    );
  };

  const pinConversation = (params: any) => {
    postAPIAsync(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.PinConversation,
      {
        ucnum: decodeURIComponent(userDetails.ucnum),
        pinRequest: params,
      },
      () => {
        setNewChatHistoryData(params, "pin", true);
      },
      () => {
        windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, WEBSITE_PATH);
      }
    );
  };

  const unPinConversation = (params: any) => {
    deleteAPIAsync(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.UnpinConversation,
      {
        ucnum: decodeURIComponent(userDetails.ucnum),
        pinRequest: params,
      },
      () => {
        setNewChatHistoryData(params, "pin", false);
      },
      () => {
        windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, WEBSITE_PATH);
      }
    );
  };

  const muteConversation = (params: any) => {
    postAPIAsync(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.InsertMute,
      {
        ucnum: decodeURIComponent(userDetails.ucnum),
        mutedUsercodenum: params,
      },
      () => {
        setNewChatHistoryData(params, "mute", true);
      },
      () => {
        windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, WEBSITE_PATH);
      }
    );
  };

  const unMuteConversation = (params: any) => {
    deleteAPIAsync(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.InsertMute,
      {
        ucnum: decodeURIComponent(userDetails.ucnum),
        mutedUsercodenum: params,
      },
      () => {
        setNewChatHistoryData(params, "mute", false);
      },
      () => {
        windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, WEBSITE_PATH);
      }
    );
  };

  const deleteConversation = (params: any) => {
    postAPIAsync(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.DeleteConversation,
      {
        ucnum: userDetails ? userDetails.ucnum : tempUserDetails.current.ucnum,
        deleteConversations: params,
      },
      () => {
        setNewChatHistoryData(params, "delete", true);
      },
      (e: any) => {
        windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, WEBSITE_PATH);
      }
    );
  };
  //#endregion

  //#region Function calls
  const handleWindowMessageReceived = (event: any) => {
    if (event.origin && event.origin.includes(WEBSITE_PATH.slice(0, -1))) {
      const data = event.data;
      if (data.type === "userdetails") {
        SetUserData(usercodenum.current, data.userDetails, searchParam.get("sessionId"), setUserDetails, (details: any) => {
          tempUserDetails.current = details;
          commonFunctionCalls(usercodenum.current);
        });
      } else if (data.activity && data.activity === "success") {
        if (userDetails && userDetails.ucnum) {
          getUserConnectivityStatus(userDetails.ucnum);
        } else if (tempUserDetails.current && tempUserDetails.current.ucnum) {
          getUserConnectivityStatus(tempUserDetails.current.ucnum);
        }
      } else if (data.type === "refresh" || data.type === "refreshblock") {
        setTimeout(() => {
          if (userDetails && userDetails.ucnum) {
            getUserInboxConversation(userDetails.ucnum, pageNo);
            getUserSuggestionList(userDetails.ucnum);
          } else if (tempUserDetails.current && tempUserDetails.current.ucnum) {
            getUserInboxConversation(tempUserDetails.current.ucnum, pageNo);
            getUserSuggestionList(tempUserDetails.current.ucnum);
          }
          setLongPressData([]);
          windowPostMessage({ type: "updateNotification" }, WEBSITE_PATH);
          if (data.type === "refreshblock") {
            windowPostMessage({ type: "changeFollowStatus3rdParty", status: data.status, receiverID: decodeURIComponent(data.data.user.usercodenum) }, WEBSITE_PATH);
            data.data.ucnum = data.data.user.usercodenum;
            blockConversation(data.data, data.blockStatus);
          }
        }, 1000);
      } else if (data.type === "deleteChat") {
        deleteConversation(conversationToDelete.current);
      } else if (data.type === "updateConnectivityStatus") {
        updateConnectivityStatus(data.entityCodeNum, data.action, data.status);
      } else if (data.type === "suggestionData") {
        if (data.data && data.data.length > 0) {
          setSuggestion(data.data);
        } else {
          getUserSuggestionList(usercodenum.current);
        }
      } else if (data.type === "typing") {
        updateTypingStatus(data.entityCodeNum, data.action);
      }
    }
  };

  const updateConnectivityStatus = (entityCodeNum: string, action: string, status: boolean) => {
    const currChatHistory: ChatHistory[] = [...chatHistoryRef.current];
    if (currChatHistory.length > 0) {
      const chatToUpdate: ChatHistory | undefined = currChatHistory.find((el) => el.user?.usercodenum === encodeURIComponent(entityCodeNum));
      if (chatToUpdate && chatToUpdate.user) {
        chatToUpdate.user.connectivityStatus = action === "mute" ? status : true;
        chatToUpdate.user.connectivityTstamp = action === "leave" ? new Date().getTime() : null;
      }
    }
    setChatHistory(currChatHistory);
  };

  const updateTypingStatus = (entityCodeNum: string, action: boolean) => {
    const currChatHistory: ChatHistory[] = [...chatHistoryRef.current];
    if (currChatHistory.length > 0) {
      const chatToUpdate: ChatHistory | undefined = currChatHistory.find((el) => el.user?.usercodenum === encodeURIComponent(entityCodeNum));
      if (chatToUpdate) {
        chatToUpdate.isTyping = action;
      }
    }
    setChatHistory(currChatHistory);
  };

  const commonFunctionCalls = (ucnum: string) => {
    getUserConnectivityStatus(ucnum);
    getUserFollowingList(ucnum);
    getUserNewFollowersCount(ucnum);
  };

  const onPinUnpinMenuCallback = (pinType: string) => {
    switch (pinType) {
      case "pin":
        const filterPinData = longPressData.filter((el) => el.pinnedAt === 0);
        const pinParam = filterPinData.map((v: ChatHistory) => {
          return {
            pinnedBy: userDetails.ucnum,
            conversationID: v.conversationID,
            pinnedAt: 0,
          };
        });

        pinConversation(pinParam);
        break;
      case "unpin":
        const filterUnPinData = longPressData.filter((el) => el.pinnedAt > 0);
        const unPinParam = filterUnPinData.map((v: ChatHistory) => {
          return {
            pinnedBy: userDetails.ucnum,
            conversationID: v.conversationID,
          };
        });
        unPinConversation(unPinParam);
        break;
    }
  };

  const onMuteUnmuteMenuCallback = (muteType: string) => {
    switch (muteType) {
      case "mute":
        const filterMuteData = longPressData.filter((el) => !(el.user && el.user.isMuted));
        const muteParam = filterMuteData.map((v: ChatHistory) => {
          return {
            mutedUcnum: v.user?.usercodenum,
            conversationID: v.conversationID,
          };
        });

        muteConversation(muteParam);
        break;
      case "unmute":
        const filterUnMuteData = longPressData.filter((el) => el.user && el.user.isMuted);
        const unMuteParam = filterUnMuteData.map((v: ChatHistory) => {
          return {
            mutedUcnum: v.user?.usercodenum,
            conversationID: v.conversationID,
          };
        });

        unMuteConversation(unMuteParam);
        break;
    }
  };

  const onDeleteMenuCallback = () => {
    const deleteParam = longPressData.map((v: ChatHistory) => {
      return {
        conversationID: v.conversationID,
        isActive: false,
      };
    });
    conversationToDelete.current = deleteParam;
    onDeleteMessageDialog();
  };

  const onDeleteMessageDialog = () => {
    windowPostMessage({ type: "userDeleteBlock", deleteType: "inbox" }, WEBSITE_PATH);
  };

  const onMenuClickCallBack = (type: string, data: ChatHistory, message?: string | null) => {
    if (data.user) {
      const index = chatHistory.findIndex((el) => el === data);
      switch (type) {
        case "mute":
          muteConversation([{ mutedUcnum: data.user?.usercodenum, conversationID: encodeURIComponent(data.conversationID) }]);
          break;
        case "unmute":
          unMuteConversation([{ mutedUcnum: data.user?.usercodenum, conversationID: encodeURIComponent(data.conversationID) }]);
          break;
        case "pin":
          pinConversation([
            {
              pinnedBy: userDetails.ucnum,
              conversationID: data.conversationID,
              pinnedAt: 0,
            },
          ]);
          break;
        case "unpin":
          unPinConversation([
            {
              pinnedBy: userDetails.ucnum,
              conversationID: data.conversationID,
            },
          ]);

          break;
        case "sendhi":
          const currentTime = new Date(new Date().toISOString()).getTime();
          data.lastActiveAt = currentTime;
          data.lastMessage.messageContent = message;
          data.lastMessage.messageType = 2;
          data.lastMessage.sentAt = currentTime;
          setNewDataToHistory(index, data);
          break;
        case "delete":
          conversationToDelete.current = [
            {
              conversationID: data.conversationID,
              isActive: false,
            },
          ];
          onDeleteMessageDialog();
          break;
      }
    }
  };

  const blockConversation = (data: any, blockStatus: string) => {
    createAndSendMessage("", blockStatus === "block" ? 7 : 8, data);
  };

  const setNewChatHistoryData = (params: any, type: string, action: boolean) => {
    setChatHistory((prevChatHistory) => {
      params.forEach((v: ChatHistory) => {
        const data: any = prevChatHistory.find((el: ChatHistory) => el.conversationID === decodeURIComponent(v.conversationID));
        switch (type) {
          case "pin":
            const index = prevChatHistory.findIndex((el: ChatHistory) => el.conversationID === decodeURIComponent(v.conversationID));
            if (index !== -1) {
              prevChatHistory[index].pinnedAt = action ? new Date().getTime() : 0;
              prevChatHistory.sort((a, b) => b.pinnedAt - a.pinnedAt);
            }
            // data.pinnedAt = action ? new Date().getTime() : 0;
            createAndSendMessage(action.toString(), 15, data);
            windowPostMessage({ type: "snackMsg", snackMsg: action ? resource.userPinned : resource.userUnPinned }, WEBSITE_PATH);
            break;
          case "mute":
            data.user.isMuted = action;
            windowPostMessage({ type: "userMuted", chatID: removeSpecialChars(decodeURIComponent(data.user.usercodenum)), action: action }, WEBSITE_PATH);
            createAndSendMessage(action.toString(), 12, data);
            windowPostMessage({ type: "snackMsg", snackMsg: action ? resource.userMute : resource.userUnMute }, WEBSITE_PATH);
            break;
          case "delete":
            isBrowser && data.user && windowPostMessage({ type: "closeChat", chatID: removeSpecialChars(decodeURIComponent(data.user.usercodenum)) }, WEBSITE_PATH);
            prevChatHistory = prevChatHistory.filter((item: any) => {
              return item !== data;
            });
            break;
        }
        if (type !== "delete") {
          const index = prevChatHistory.indexOf(data);
          if (!isNullOrEmpty(data) && index !== -1) {
            prevChatHistory[index] = data;
          }
        }
      });
      return prevChatHistory;
    });
    setLongPressData([]);
  };

  const setNewDataToHistory = (index: number, newData: ChatHistory) => {
    if (index > -1) {
      const tempData = [...chatHistory];
      tempData[index] = newData;
      setChatHistory(tempData);
    }
  };

  const createAndSendMessage = (messageContent: string, messageType: number, data: ChatHistory) => {
    if (data.user) {
      const message: MessageData = {
        conversationID: data.conversationID,
        messageID: "",
        messageContent: Encrypt(messageContent),
        messageType: messageType,
        recipientUcnum: data.user?.usercodenum,
        isRead: false,
        deletedBy: "",
        messageStatus: 0,
        sentAt: new Date(new Date().toISOString()).getTime(),
      };
      const messageConnection: Connection = Connector(data.conversationID, () => {
        messageConnection.newMessage(JSON.stringify(message), decodeURIComponent(tempUserDetails.current.ucnum), data.conversationID);
      });
      if (messageConnection) {
        messageConnection.newMessage(JSON.stringify(message), decodeURIComponent(tempUserDetails.current.ucnum), data.conversationID);
      }
    }
  };

  const onScrollEvent = (e: any) => {
    const $eTarget = e.currentTarget;
    hideMenuRef.current?.hideMenu();
    if (!isAjax || !($eTarget.scrollTop + $eTarget.clientHeight >= $eTarget.firstChild.clientHeight)) {
      return;
    }
    setLoader(true);
    setIsAjax(false);
    setPageNo(pageNo + MAX_RECORD);
  };

  //#endregion

  return (
    <Box className="inboxWindow flex-column pad-top-gutter pad-bottom-gutter">
      {userDetails && longPressData.length === 0 && <CreateHeaderInbox isNewChat={newChat} connectivityStatus={connectivityStatus} userId={userDetails.ucnum} sessionId={userDetails.sessionID} />}
      {longPressData.length > 0 && (
        <CreateMenuHeader
          backCallback={() => {
            setLongPressData([]);
          }}
          arrMenuOptions={mobMenuOptions}
          dataCount={longPressData.length}
        />
      )}
      {loaderPage ? (
        <InboxSkeleton />
      ) : (
        <Box className="inboxContentContainer pad-l-r-gutter" onScroll={onScrollEvent}>
          <Box className="flex-column">
            <ListItemButton className={`icon-button-parent pad-l-0 pad-r-0 pad-t-12 pad-b-12 ${chatHistory.length > 0 ? "mar-b-8" : ""}`}>
              <Box
                className="flex-row cur-pointer flex-center newFollowers w-per-100"
                onClick={() => {
                  navigate(`/followers?userId=${userDetails.ucnum}&sessionId=${userDetails.sessionID}&since=0`);
                }}
              >
                <Box className="bg-summer-sky flex pad-12 bor-rad-50">{icon({ iconName: "group", iconColor: "icon-true-white" })}</Box>
                <Box className="flex-1 flex-column mar-l-8 flex-justify-start pad-r-13">
                  <Typography variant="subtitle2" className="text-primary text-align-left">
                    New followers
                  </Typography>

                  <Typography variant="caption" className={`clamp-1 ${newFollowersCount > 0 ? "text-modern-gray" : "text-secondary"} mar-t-6`}>
                    {!isNullOrEmpty(newFollowersName) ? `${newFollowersName} started following you` : resource.followerMsg}
                  </Typography>
                </Box>
                <Box className="flex" style={{ width: "94px", justifyContent: "flex-end" }}>
                  <Box className={`icon-button flex-center ${newFollowersCount === 0 ? "box-32" : ""}`}>{newFollowersCount > 0 ? <StyledBadgeNotification badgeContent={newFollowersCount}></StyledBadgeNotification> : icon({ iconName: "chevron_right", iconColor: "icon-secondary" })}</Box>
                </Box>
              </Box>
            </ListItemButton>
            {chatHistory.length > 0 && <ListCards data={chatHistory} type="chathistory" ref={hideMenuRef} actionCallback={onMenuClickCallBack} connectivityStatus={connectivityStatus} addArrData={setLongPressData} arrData={longPressData} secondaryActionWidth="94px" />}
            {loader && (
              <Box className="flex-center ht-64" sx={{ m: 2 }}>
                <CircularProgress className="box-24" />
              </Box>
            )}
          </Box>
          {suggestion.length > 0 && (
            <Box className="flex-column mar-t-16">
              <Box className="flex-row flex-align-center mar-b-8 ht-48" sx={{ paddingLeft: isMobileOnly ? "24px" : "28px" }}>
                <TooltipMUI icon={icon({ iconName: "info", iconSize: "size-16", iconColor: "icon-spanish-gray", iconCustomClass: "cur-pot mdc-soft-show", iconStyle: { marginLeft: "8px" } })} tooltipText={`${resource.suggestionInfo}`} text="Suggested for you" />
              </Box>
              {newChat && (
                <Box className={`pad-l-0 pad-r-0 pad-t-12 pad-b-12 mar-b-8`}>
                  <Box className="flex-row flex-center w-per-100">
                    <Box className="bg-office-green flex pad-12 bor-rad-50">{icon({ iconName: "contacts", iconColor: "icon-true-white" })}</Box>
                    <Box className="flex-1 flex-column mar-l-8 flex-justify-start pad-r-13">
                      <Typography variant="subtitle2" className="text-primary text-align-left">
                        Chat with friends
                      </Typography>

                      <Typography variant="caption" className={`clamp-1 text-secondary mar-t-6`}>
                        Find and chat with them
                      </Typography>
                    </Box>
                    <Box style={{ width: "94px", justifyContent: "flex-end" }}>
                      <Button
                        variant="contained"
                        size="small"
                        className="small-btn"
                        onClick={(e) => {
                          navigate(`/chats?userId=${userDetails.userId}&sessionId=${userDetails.sessionId}`);
                        }}
                      >
                        Find
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
              <CardListForSuggestion data={suggestion} state={setSuggestion} secondaryActionWidth="94px" />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Inbox;
